import apiService from './api';

export interface CustomerReportRow {
  id: number;
  customer_name: string;
  phone: string;
  amount: number;
  service_charge: number;
  total_amount: number;
  total_received: number;
  balance: number;
}

export interface OverallReportRow {
  customer_name: string;
  phone: string;
  total_amount: number | null;
  received_amount_description: string | null;
  item_description: string | null;
  account_head: string | null;
  received_amount: number | null;
  amount: number | null;
  type: 'Invoice' | 'Received Amount';
  date: string;
}

interface OverallReportFilters {
  customer_id?: string;
  account_head_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface GstReportRow {
  name: string;
  account_head: string;
  total_amount: number;
  gst_amount: number;
}

interface GstReportFilters {
  account_head_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface EmployeeReportRow {
  customer_name: string;
  phone_number: string;
  description: string;
  employee_name: string;
  total_amount: number | null;
  account_head: string | null;
  type: 'Invoice' | 'Received Amount';
  date: string;
}

export interface RevenueReportRow {
  customer_name: string;
  phone_number: string;
  total_amount: number;
  service_charge: number;
  account_head: string;
  description: string;
  employee_name: string;
  type: 'Invoice' | 'Received Amount';
  date: string;
}

interface RevenueReportFilters {
  account_head_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface BalanceReportRow {
  id: string;
  customer_name: string;
  phone: string;
  total_amount: number;
  received_amount: number;
  balance: number;
}

export interface BalanceReportDetailRow {
  date: string;
  type: 'invoice' | 'received';
  total_amount: number;
  received_amount: number;
  running_balance: number;
}

export interface BalanceReportDetail {
  customer: {
    name: string;
    phone: string;
  };
  transactions: BalanceReportDetailRow[];
}

export interface BalanceTransactionRow {
  id: string;
  customer_name: string;
  phone: string;
  date: string;
  total_amount: number;
  received_amount: number;
  type: 'invoice' | 'received';
  running_balance: number;
}

class ReportService {
  async getCustomerReport(): Promise<CustomerReportRow[]> {
    const response = await apiService.get<CustomerReportRow[]>('/reports/customer');
    return response.data;
  }
  async getOverallReport(filters: OverallReportFilters = {}): Promise<OverallReportRow[]> {
    const params = new URLSearchParams();
    if (filters.customer_id) params.append('customer_id', filters.customer_id);
    if (filters.account_head_id) params.append('account_head_id', filters.account_head_id);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    const response = await apiService.get<OverallReportRow[]>(`/reports/overall?${params.toString()}`);
    return response.data;
  }
  async getGstReport(filters: GstReportFilters = {}): Promise<GstReportRow[]> {
    const params = new URLSearchParams();
    if (filters.account_head_id) params.append('account_head_id', filters.account_head_id);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    const response = await apiService.get<GstReportRow[]>(`/reports/gst?${params.toString()}`);
    return response.data;
  }
  async fetchEmployeeReport(params: {
    employee_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<EmployeeReportRow[]> {
    const response = await apiService.get<EmployeeReportRow[]>('/reports/employee', { params });
    return response.data;
  }
  async getRevenueReport(filters: RevenueReportFilters = {}): Promise<RevenueReportRow[]> {
    const params = new URLSearchParams();
    if (filters.account_head_id) params.append('account_head_id', filters.account_head_id);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    const response = await apiService.get<RevenueReportRow[]>(`/reports/revenue?${params.toString()}`);
    return response.data;
  }
  async getBalanceReport(): Promise<BalanceReportRow[]> {
    const response = await apiService.get<BalanceReportRow[]>('/reports/balance');
    return response.data;
  }

  async getBalanceReportDetail(customerId: string): Promise<BalanceReportDetail> {
    const response = await apiService.get<BalanceReportDetail>(`/reports/balance/${customerId}`);
    return response.data;
  }

  async getBalanceTransactions(customer_id: string, from_date: string, to_date: string): Promise<{ transactions: BalanceTransactionRow[]; old_balance: number }> {
    const response = await apiService.post<{ transactions: BalanceTransactionRow[]; old_balance: number }>('/reports/balance/transactions', {
      customer_id, from_date, to_date
    });
    return response.data;
  }
}

const reportService = new ReportService();
export default reportService; 
